import { APIGEE_MGMT_API_BASE_URL } from '@openx/constants/api.ts';
import ky, { isHTTPError, type KyInstance } from 'ky';
import { applyWorkAsHeadersFromLocalStorage } from '../lib/applyWorkAsHeadersFromLocalStorage.ts';
import { getXOpenxInstanceHeader } from '../lib/getXOpenxInstance.ts';
import { getOIDCAuthInfoFromStorage } from '../lib/oidc/getOIDCAuthInfoFromStorage.ts';
import { redirectToForbiddenPage } from '../routes/redirectToForbiddenPage.ts';
import { logoutOnError } from './logoutOnError.ts';

export type CreateHttpClientParams = {
  clientHeader: string;
  clientHeaderValue: string;
  pageHeader: string;
};

export const createHttpClient = ({
  clientHeader,
  clientHeaderValue,
  pageHeader,
}: CreateHttpClientParams): KyInstance => {
  return ky.create({
    // Retries are handled by the tanstack query library
    retry: 0,
    baseUrl: APIGEE_MGMT_API_BASE_URL,
    headers: {
      [clientHeader]: clientHeaderValue,
    },
    hooks: {
      beforeRequest: [
        async ({ request }) => {
          request.headers.set(pageHeader, window.location.pathname);
          applyWorkAsHeadersFromLocalStorage(request.headers);

          const oidcAuthInfo = await getOIDCAuthInfoFromStorage();

          if (oidcAuthInfo) {
            request.headers.set('Authorization', `Bearer ${oidcAuthInfo.accessToken}`);
            request.headers.set('x-openx-instance', getXOpenxInstanceHeader(oidcAuthInfo.instanceUid));
          }

          return request;
        },
      ],
      beforeError: [
        ({ error, options }) => {
          if (!isHTTPError(error)) {
            return error;
          }

          const status = error.response.status;
          const skipGlobalForbiddenHandling = options.skipGlobalForbiddenHandling;

          if (status === 401) {
            logoutOnError();
          } else if (status === 403 && !skipGlobalForbiddenHandling) {
            redirectToForbiddenPage();
          }

          return error;
        },
      ],
    },
  });
};
